import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined'
import FlashOnOutlinedIcon from '@mui/icons-material/FlashOnOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { styled } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid2 from '@mui/material/Grid2'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'

import Loader from '@/app/component/atom/loader'
import useGetBilling from '@/app/module/dashboard/hooks/use-get-billing'
import { useGetPlan } from '@/app/module/plans/hooks/use-get-plan'
import { useGetUser } from '@/app/module/user/hooks/use-get-user'
import { selectActiveOrg } from '@/app/module/user/store/selectors'
import { formatName } from '@/app/service/util/format'
import { Organization } from '@/app/types'

import EmptyAccountBanner from './empty-account-banner'
import InlineFeedbackForm from './inline-feedback-form'
import OrgWarning from './org-warning'
import PeriodUsage from './period-usage'
import PlanDetails from './plan-details'
import QuickActions from './quick-actions'
import RequestForm from './request-form'
import { SectionHeader } from './section'

const Dashboard: React.FC = () => {
  const navigate = useNavigate()
  const { isFetching: isBillingLoading } = useGetBilling()
  const { isFetching: isPlanLoading, data: plan } = useGetPlan()
  const { data: userData, isFetching: isUserLoading } = useGetUser()
  const activeOrg = useSelector(selectActiveOrg) as Organization
  const userFullName = React.useMemo(() => {
    if (!userData) {
      return 'there'
    }

    return formatName(userData.firstName, userData.lastName) || userData.username
  }, [userData])

  const loading = isBillingLoading || isPlanLoading || isUserLoading

  const { greeting, emoji: greetingEmoji } = React.useMemo(() => getGreetingParts(), [])

  const isTrial = plan && !plan.planId
  const trialDaysLeft = plan?.trialDaysLeft
  const trialActive = isTrial && typeof trialDaysLeft === 'number' && trialDaysLeft > 0
  const trialExpired = isTrial && typeof trialDaysLeft === 'number' && trialDaysLeft <= 0

  const toSubscription = () => navigate('/organization/subscription')

  return (
    <Container>
      <OrgWarning />
      <Box my={4}>
        <Typography component="h1" variant="h5" fontWeight={600} sx={{ position: 'relative', display: 'inline-block' }}>
          <span role="img" aria-label="greeting" style={{ marginRight: 6 }}>
            {greetingEmoji}
          </span>
          {greeting}, <NameHighlight>{userFullName}</NameHighlight>!
          <AccentBar />
        </Typography>
      </Box>
      <Grid2
        container
        spacing={{
          sm: 0,
          md: 2,
        }}
      >
        <Grid2 size={{ xs: 12, md: 8 }}>
          <Card sx={{ mb: 2, p: 0 }}>
            <CardContent sx={{ p: 3 }}>
              {!!activeOrg && (
                <Box mb={2} data-testid="active-organization">
                  <SectionHeader
                    icon={<DomainOutlinedIcon sx={{ fontSize: 26 }} />}
                    title={
                      <>
                        Your organization <strong>{activeOrg.name}</strong> ({activeOrg.orgId})
                      </>
                    }
                  />

                  <Typography variant="subtitle2" sx={{ mt: 1 }}></Typography>
                </Box>
              )}
              <EmptyAccountBanner />
              {isTrial ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  gap={1.5}
                  mb={3}
                  mt={1}
                >
                  {trialActive && (
                    <>
                      <Typography variant="h6">You are currently using the 14 day trial.</Typography>
                      <Typography variant="body1">
                        It will expire in {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'}.
                      </Typography>
                    </>
                  )}
                  {trialExpired && <Typography variant="h6">You are currently not on a subscription plan.</Typography>}
                  <Button variant="contained" color="primary" onClick={toSubscription}>
                    Select subscription plan
                  </Button>
                </Box>
              ) : (
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={6} mb={3} alignItems="flex-start">
                  <Box flex={1}>
                    <PeriodUsage />
                  </Box>
                  <Box flex={1}>
                    <PlanDetails />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 2, p: 0 }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader icon={<HelpOutlineIcon sx={{ fontSize: 26 }} />} title="I need something!" />
              {/** padding left here is 3px to make up for the checkboxes margin */}
              <Box mt={1} pl="3px">
                <RequestForm />
              </Box>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 4 }}>
          <BorderedCard sx={{ p: 0 }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader icon={<FlashOnOutlinedIcon sx={{ fontSize: 26 }} />} title="Need help? We're here!" />
              <Box mt={2}>
                <QuickActions />
              </Box>
            </CardContent>
          </BorderedCard>

          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader icon={<FeedbackOutlinedIcon sx={{ fontSize: 26 }} />} title="Send Feedback" />
              <Box mt={2}>
                <InlineFeedbackForm />
              </Box>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
      {loading && <Loader />}
    </Container>
  )
}

const Container = styled('div')(({ theme }) => ({
  margin: 'auto',
  marginTop: 16,
  padding: '0 1rem',
  width: '100%',

  [theme.breakpoints.down('md')]: {
    paddingBottom: 40,
  },
}))

const BorderedCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: 'none',
}))

const NameHighlight = styled('span')(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(25,118,210,0.10)',
  padding: '2px 8px',
  borderRadius: 6,
}))

const AccentBar = styled('span')(({ theme }) => ({
  position: 'absolute',
  left: 0,
  bottom: -10,
  height: 4,
  width: '70%',
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
  borderRadius: 2,
  pointerEvents: 'none',
}))

const getGreeting = (hour: number): string => {
  if (hour < 5 || hour >= 22) return 'Welcome'
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

const getEmoji = (hour: number): string => {
  if (hour < 5 || hour >= 22) return '🌙'
  if (hour < 12) return '☀️'
  if (hour < 18) return '🌤️'
  return '🌇'
}

export const getGreetingParts = (date: Date = new Date()): { greeting: string; emoji: string } => {
  const hour = date.getHours()

  return {
    greeting: getGreeting(hour),
    emoji: getEmoji(hour),
  }
}

export default Dashboard
